import {
  DEFAULT_MIN_NAVIGATOR_LENGTH,
  POINT_SIZE,
  transformLabelPosition,
} from "components/ui/KeyLabel";
import {
  KeyLabel,
  KeyplanOrientation,
} from "interfaces/models/documentKeyplan";
import { distanceBetweenTwoPoint } from "models/documentTask";
import { keyplanStore } from "stores/keyplan/store";
import { checkKeyPlanOrientationByAngle } from "utils/keynote";

export class MoveKeylabelHelper {
  private id?: string;
  private label?: string;
  private iconWidth: number;
  private iconHeight: number;
  private pointElement: HTMLElement;
  private lineElement: HTMLElement;
  private positionInit: { x: number; y: number };
  private distanceBouding?: { top: number; left: number };
  private imageRotation: number;
  private callbackDragStop?: (props: Partial<KeyLabel>) => void;
  private interactionType: "mouse" | "touch" | null = null;

  // State variables
  private pos1: number = 0;
  private pos2: number = 0;
  private pos3: number = 0;
  private pos4: number = 0;
  private isDragged: boolean = false;
  private navigatorLengthTemp: number = DEFAULT_MIN_NAVIGATOR_LENGTH;
  private newRotation: number;
  private rotationTemp: number;
  private positionOfPointTemp: { x: number; y: number };
  private positionOfPoint: { x: number; y: number };
  private isLandscape: boolean;
  private imageWidth: number;
  private imageHeight: number;

  // Touch-specific tracking
  private touchIdentifier: number | null = null;
  private initialElementTop: number = 0;
  private initialElementLeft: number = 0;
  private initialTouchX: number = 0;
  private initialTouchY: number = 0;

  constructor({
    id,
    label,
    iconWidth,
    iconHeight,
    rotationInit,
    pointElement,
    lineElement,
    positionInit,
    distanceBouding,
    imageSize,
    imageRotation = 0,
    callbackDragStop,
  }: {
    id?: string;
    label?: string;
    rotationInit: number;
    positionInit: { x: number; y: number };
    iconWidth: number;
    iconHeight: number;
    imageSize: { width: number; height: number };
    pointElement: HTMLElement;
    lineElement: HTMLElement;
    distanceBouding?: { top: number; left: number };
    imageRotation?: number;
    callbackDragStop?: (props: Partial<KeyLabel>) => void;
  }) {
    this.id = id;
    this.label = label;
    this.iconWidth = iconWidth;
    this.iconHeight = iconHeight;
    this.pointElement = pointElement;
    this.lineElement = lineElement;
    this.positionInit = positionInit;
    this.distanceBouding = distanceBouding;
    this.imageRotation = imageRotation;
    this.callbackDragStop = callbackDragStop;

    // Initialize calculated properties
    this.newRotation =
      rotationInit < 0 ? 360 - Math.abs(rotationInit) : rotationInit;
    this.rotationTemp =
      rotationInit < 0 ? 360 - Math.abs(rotationInit) : rotationInit;

    this.positionOfPointTemp = {
      x:
        pointElement.offsetLeft +
        positionInit.x +
        iconHeight / 2 -
        POINT_SIZE / 2,
      y:
        pointElement.offsetTop +
        positionInit.y +
        iconWidth / 2 -
        POINT_SIZE / 2,
    };

    this.positionOfPoint = this.positionOfPointTemp;

    this.isLandscape =
      checkKeyPlanOrientationByAngle(imageRotation) ===
      KeyplanOrientation.LANDSCAPE;

    this.imageWidth = imageSize.width - iconWidth;
    this.imageHeight = imageSize.height - iconHeight;

    if (this.isLandscape) {
      [this.imageWidth, this.imageHeight] = [this.imageHeight, this.imageWidth];
    }

    // Bind methods to ensure 'this' context
    this.dragMouseDown = this.dragMouseDown.bind(this);
    this.elementDrag = this.elementDrag.bind(this);
    this.closeDragElement = this.closeDragElement.bind(this);
    this.dragTouchStart = this.dragTouchStart.bind(this);
    this.elementTouchMove = this.elementTouchMove.bind(this);
    this.closeTouchElement = this.closeTouchElement.bind(this);

    // Set up initial event handlers
    this.initialize();
  }

  /**
   * Initialize the drag functionality with both mouse and touch events
   */
  public initialize(): void {
    // Mouse events
    this.pointElement.onmousedown = this.dragMouseDown;

    // Touch events - use the direct DOM API for better control
    this.pointElement.addEventListener("touchstart", this.dragTouchStart, {
      passive: false,
    });
  }

  /**
   * Clean up event listeners
   */
  public destroy(): void {
    // Clean up mouse events
    this.pointElement.onmousedown = null;
    document.onmouseup = null;
    document.onmousemove = null;

    // Clean up touch events
    this.pointElement.removeEventListener("touchstart", this.dragTouchStart);
    document.removeEventListener("touchmove", this.elementTouchMove);
    document.removeEventListener("touchend", this.closeTouchElement);
    document.removeEventListener("touchcancel", this.closeTouchElement);
  }

  /**
   * Helper method to rotate a point by a given angle
   */
  private rotatePoint(
    x: number,
    y: number,
    angle: number
  ): { x: number; y: number } {
    switch (angle) {
      case 90:
        return { x: y, y: -x };
      case 180:
        return { x: -x, y: -y };
      case 270:
        return { x: -y, y: x };
      default:
        return { x, y };
    }
  }

  /**
   * Helper method to scale a point based on page ratio
   */
  private scalePoint(x: number, y: number): { x: number; y: number } {
    const pageRatio = keyplanStore.getState().pageRatioEditMode;
    if (!pageRatio) {
      return { x, y };
    }

    return { x: Math.floor(x / pageRatio), y: Math.floor(y / pageRatio) };
  }

  /**
   * Handle mouse down event to start dragging
   */
  private dragMouseDown(e: MouseEvent): void {
    if (this.interactionType === "touch") return;
    this.interactionType = "mouse";
    e.stopPropagation();
    e = e || window.event;
    e.preventDefault();
    const rotatePos = this.rotatePoint(
      e.clientX,
      e.clientY,
      this.imageRotation
    );
    const pos = this.scalePoint(rotatePos.x, rotatePos.y);

    this.pos3 = pos.x;
    this.pos4 = pos.y;
    this.isDragged = false;

    document.addEventListener("mouseup", this.closeDragElement);
    document.addEventListener("mousemove", this.elementDrag);

    this.positionOfPointTemp = {
      x:
        this.pointElement.offsetLeft +
        this.positionInit.x +
        this.iconHeight / 2 -
        POINT_SIZE / 2,
      y:
        this.pointElement.offsetTop +
        this.positionInit.y +
        this.iconWidth / 2 -
        POINT_SIZE / 2,
    };
  }

  /**
   * Handle touch start event (mobile equivalent of mouse down)
   */
  private dragTouchStart(e: TouchEvent): void {
    // Only process first touch
    if (e.touches.length !== 1) return;
    if (this.interactionType === "mouse") return;
    this.interactionType = "touch";

    e.stopPropagation();
    e.preventDefault();

    const touch = e.touches[0];

    // Store the touch identifier to track this specific touch
    this.touchIdentifier = touch.identifier;

    // Store initial element position
    this.initialElementTop = this.pointElement.offsetTop;
    this.initialElementLeft = this.pointElement.offsetLeft;

    // Store initial touch position
    this.initialTouchX = touch.clientX;
    this.initialTouchY = touch.clientY;

    // Calculate position in the appropriate coordinate system
    const rotatePos = this.rotatePoint(
      touch.clientX,
      touch.clientY,
      this.imageRotation
    );
    const pos = this.scalePoint(rotatePos.x, rotatePos.y);

    this.pos3 = pos.x;
    this.pos4 = pos.y;
    this.isDragged = false;

    // Add event listeners for touch events
    document.addEventListener("touchmove", this.elementTouchMove, {
      passive: false,
    });
    document.addEventListener("touchend", this.closeTouchElement, {
      passive: false,
    });
    document.addEventListener("touchcancel", this.closeTouchElement, {
      passive: false,
    });

    this.positionOfPointTemp = {
      x:
        this.pointElement.offsetLeft +
        this.positionInit.x +
        this.iconHeight / 2 -
        POINT_SIZE / 2,
      y:
        this.pointElement.offsetTop +
        this.positionInit.y +
        this.iconWidth / 2 -
        POINT_SIZE / 2,
    };
  }

  /**
   * Handle element dragging
   */
  private elementDrag(e: MouseEvent): void {
    e.stopPropagation();
    e = e || window.event;
    e.preventDefault();
    const { deltaX, deltaY, scalePoint } = this.calculateDelta(
      e.clientX,
      e.clientY,
      this.pos3,
      this.pos4
    );

    this.pos1 = deltaX;
    this.pos2 = deltaY;
    this.pos3 = scalePoint.x;
    this.pos4 = scalePoint.y;

    this.updateElementPosition();
  }

  /**
   * Handle touch move event (mobile equivalent of mouse move)
   */
  private elementTouchMove(e: TouchEvent): void {
    e.stopPropagation();
    e.preventDefault();

    // Find our tracked touch
    let activeTouch: Touch | undefined;
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === this.touchIdentifier) {
        activeTouch = e.changedTouches[i];
        break;
      }
    }

    // If we couldn't find our touch, exit
    if (!activeTouch) return;

    // Calculate the difference between current and initial touch positions
    const rotateActive = this.rotatePoint(
      activeTouch.clientX,
      activeTouch.clientY,
      this.imageRotation
    );
    const scaleActiveTouch = this.scalePoint(rotateActive.x, rotateActive.y);
    const { deltaX: touchDeltaX, deltaY: touchDeltaY } = this.calculateDelta(
      this.initialTouchX,
      this.initialTouchY,
      scaleActiveTouch.x,
      scaleActiveTouch.y
    );

    // Calculate new element position based on original position plus delta
    const newTop = this.initialElementTop + touchDeltaY;
    const newLeft = this.initialElementLeft + touchDeltaX;

    // Update position for boundaries check
    let leftAbsolute =
      newLeft + this.positionInit.x + (this.distanceBouding?.left || 0);
    let topAbsolute =
      newTop + this.positionInit.y + (this.distanceBouding?.top || 0);

    if (this.isLandscape) {
      [leftAbsolute, topAbsolute] = [topAbsolute, leftAbsolute];
    }

    // Check if position is outside boundaries
    const isOutsideBoundaries =
      leftAbsolute < 0 ||
      topAbsolute < 0 ||
      leftAbsolute > this.imageWidth ||
      topAbsolute > this.imageHeight;

    // If outside boundaries, don't update position
    if (isOutsideBoundaries) return;

    // Update element position
    this.isDragged = true;
    this.pointElement.style.top = `${newTop}px`;
    this.pointElement.style.left = `${newLeft}px`;

    // Update point position for calculations
    this.positionOfPoint = {
      x: newLeft + this.positionInit.x + this.iconWidth / 2 - POINT_SIZE / 2,
      y: newTop + this.positionInit.y + this.iconHeight / 2 - POINT_SIZE / 2,
    };

    // Update rotation and navigator length
    this.updateRotationAndNavigator();

    // Update for next movement
    this.pos3 = scaleActiveTouch.x;
    this.pos4 = scaleActiveTouch.y;
  }

  /**
   * Update element position
   */
  private updateElementPosition(): void {
    const top = this.pointElement.offsetTop - this.pos2;
    const left = this.pointElement.offsetLeft - this.pos1;

    // Calculate absolute positions with offsets
    let leftAbsolute =
      left + this.positionInit.x + (this.distanceBouding?.left || 0);
    let topAbsolute =
      top + this.positionInit.y + (this.distanceBouding?.top || 0);

    if (this.isLandscape) {
      [leftAbsolute, topAbsolute] = [topAbsolute, leftAbsolute];
    }

    // Check if position is outside boundaries
    const isOutsideBoundaries =
      leftAbsolute < 0 ||
      topAbsolute < 0 ||
      leftAbsolute > this.imageWidth ||
      topAbsolute > this.imageHeight;

    // If outside boundaries, don't update position
    if (isOutsideBoundaries) return;

    // Only update position if within boundaries
    this.isDragged = true;
    this.pointElement.style.top = `${top}px`;
    this.pointElement.style.left = `${left}px`;

    this.positionOfPoint = {
      x: left + this.positionInit.x + this.iconWidth / 2 - POINT_SIZE / 2,
      y: top + this.positionInit.y + this.iconHeight / 2 - POINT_SIZE / 2,
    };

    this.updateRotationAndNavigator();
  }

  /**
   * common method to calculate delta for mouse and touch event
   */
  private calculateDelta(
    currentX: number,
    currentY: number,
    initialX: number,
    initialY: number
  ): { deltaX: number; deltaY: number; scalePoint: { x: number; y: number } } {
    const rotatePos = this.rotatePoint(currentX, currentY, this.imageRotation);
    const pos = this.scalePoint(rotatePos.x, rotatePos.y);

    return {
      deltaX: initialX - pos.x,
      deltaY: initialY - pos.y,
      scalePoint: pos,
    };
  }

  /**
   * Update rotation and navigator properties
   */
  private updateRotationAndNavigator(): void {
    const prePositionOfPoint = this.positionOfPointTemp;
    const navigatorLength = distanceBetweenTwoPoint(
      this.positionOfPoint,
      this.positionInit
    );

    const edgeLength1 = distanceBetweenTwoPoint(
      prePositionOfPoint,
      this.positionInit
    );

    const edgeLength2 = distanceBetweenTwoPoint(
      prePositionOfPoint,
      this.positionOfPoint
    );

    let cos = 0;
    if (navigatorLength > 0 && edgeLength1 > 0) {
      cos =
        (Math.pow(navigatorLength, 2) +
          Math.pow(edgeLength1, 2) -
          Math.pow(edgeLength2, 2)) /
        (2 * navigatorLength * edgeLength1);

      // Ensure cos is within valid range for acos
      cos = Math.max(-1, Math.min(1, cos));
    }

    let deg = (Math.acos(cos) * 180) / Math.PI;

    // Check point is to the right or left side of a line
    // sign((Bx - Ax) * (Y - Ay) - (By - Ay) * (X - Ax))
    const ret = Math.sign(
      (prePositionOfPoint.x - this.positionInit.x) *
        (this.positionOfPoint.y - this.positionInit.y) -
        (prePositionOfPoint.y - this.positionInit.y) *
          (this.positionOfPoint.x - this.positionInit.x)
    );

    deg = deg * (ret || 1);

    this.newRotation = this.rotationTemp + deg;
    this.newRotation =
      this.newRotation > 360
        ? Math.abs(360 - this.newRotation)
        : this.newRotation < 0
        ? 360 - Math.abs(this.newRotation)
        : this.newRotation;

    this.lineElement.style.width = `${navigatorLength}px`;
    this.lineElement.style.transform = `rotate(${this.newRotation}deg)`;
    this.navigatorLengthTemp = navigatorLength;
  }

  /**
   * Handle closing the drag operation
   */
  private closeDragElement(e?: MouseEvent): void {
    e?.stopPropagation();
    this.finalizeElementPosition();
    this.interactionType = null;

    // Clean up mouse event listeners
    document.removeEventListener("mouseup", this.closeDragElement);
    document.removeEventListener("mousemove", this.elementDrag);
  }

  /**
   * Handle touch end event (mobile equivalent of mouse up)
   */
  private closeTouchElement(e?: TouchEvent): void {
    e?.stopPropagation();
    this.interactionType = null;

    // Reset touch tracking
    this.touchIdentifier = null;

    this.finalizeElementPosition();

    // Clean up touch event listeners
    document.removeEventListener("touchmove", this.elementTouchMove);
    document.removeEventListener("touchend", this.closeTouchElement);
    document.removeEventListener("touchcancel", this.closeTouchElement);
  }

  /**
   * Finalize element position - shared between mouse and touch handling
   */
  private finalizeElementPosition(): void {
    this.positionOfPointTemp = this.positionOfPoint;
    this.rotationTemp = this.newRotation;

    if (this.isDragged && this.id && this.label) {
      const transformRotation =
        this.rotationTemp < 0
          ? 360 - Math.abs(this.rotationTemp)
          : this.rotationTemp;

      const labelPosition = transformLabelPosition({
        transformRotation,
        navigatorLength: this.navigatorLengthTemp,
        iconWidth: this.iconWidth,
        iconHeight: this.iconHeight,
      });

      this.pointElement.style.top = null as any;
      this.pointElement.style.left = null as any;
      this.pointElement.style.right = null as any;

      Object.keys(labelPosition).forEach((key) => {
        this.pointElement.style[key as any] = labelPosition[key];
      });

      this.callbackDragStop?.({
        navigatorLength: this.navigatorLengthTemp,
        rotation: this.rotationTemp,
        label: this.label,
        id: this.id,
      });
    }

    this.isDragged = false;
  }
}
